<template>
  <!-- Mobile Card -->
  <RevCard
    class="mt-24 flex max-w-fit flex-row items-center gap-8 p-16 md:hidden"
  >
    <IconOfficialFilled size="32" />
    <div>
      <p v-if="rating" class="body-2-bold">
        {{
          i18n(translations.approvedSince, {
            rating: rating?.toPrecision(2),
            maxRating: MAX_RATING,
          })
        }}
      </p>

      <FormattedMessage class="body-2" :definition="translations.trusted">
        <template #years>
          <span class="body-2-bold">
            {{ i18n(translations.years, { years }) }}
          </span>
        </template>
      </FormattedMessage>

      <p v-if="displayCountry" class="body-2" data-allow-mismatch="text">
        {{ i18n(translations.basedIn, { country }) }}
        <Flag
          v-if="countryCode"
          aria-hidden="true"
          class="ml-2 inline-block align-baseline"
          :country-code
        />
      </p>
    </div>
  </RevCard>

  <!-- Desktop Card -->
  <RevCard class="mt-24 hidden items-center justify-start gap-8 p-16 md:flex">
    <div
      v-if="rating"
      class="flex items-center gap-4"
      :class="{ 'w-1/3': displayCountry, 'w-1/2': !displayCountry }"
    >
      <IconOfficialFilled size="24" />
      <p class="body-2-bold">
        {{
          i18n(translations.approvedSince, {
            rating: rating?.toPrecision(2),
            maxRating: MAX_RATING,
          })
        }}
      </p>
    </div>

    <div
      v-if="years"
      class="flex items-center gap-4"
      :class="{ 'w-1/3': displayCountry, 'w-1/2': !displayCountry }"
    >
      <IconCalendar size="24" />
      <FormattedMessage class="body-2" :definition="translations.trusted">
        <template #years>
          <span class="body-2-bold">
            {{ i18n(translations.years, { years }) }}
          </span>
        </template>
      </FormattedMessage>
    </div>

    <div v-if="displayCountry" class="flex w-1/3 items-center gap-4">
      <Flag
        v-if="countryCode"
        aria-hidden="true"
        class="mr-4 inline-block align-baseline"
        :country-code
      />
      <p class="body-2">
        {{ i18n(translations.basedIn, { country }) }}
      </p>
    </div>
  </RevCard>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api'
import FormattedMessage from '@backmarket/nuxt-module-i18n/FormattedMessage.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { IconCalendar } from '@ds/icons/IconCalendar'
import { IconOfficialFilled } from '@ds/icons/IconOfficialFilled'

import translations from './MainSellerInfo.translations'

const i18n = useI18n()
const MAX_RATING = 5
const COUNTRIES_WITH_FLAG: readonly MarketCountryCode[] = [
  MarketCountryCode.US,
  MarketCountryCode.AU,
  MarketCountryCode.JP,
]

const {
  country = undefined,
  countryCode = undefined,
  rating = undefined,
  years,
} = defineProps<{
  country?: string
  countryCode?: MarketCountryCode
  rating?: number
  years: number
}>()

const displayCountry = computed(() => {
  if (!countryCode) return false

  return COUNTRIES_WITH_FLAG.includes(countryCode)
})
</script>
